import './App.scss';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Container,
  Link,
  RequireFeature,
  dx3Observables,
  getRootUrlByToken,
  useAuthContext,
  useHandledLinksContext,
} from '@custom/dx3';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { COPY } from './assets/copy/copy';
import { ArticleContainer } from './containers/ArticleContainer/ArticleContainer';
import { HomeScreen } from './containers/HomeScreen/HomeScreen';
import { PrivateRoute } from './routes/PrivateRoute';
import ROUTES from './routes/routes';
import { LOGIN_ACTION } from './store/mutations/mutations';
import { GET_BASIC_USER_DATA } from './store/queries/utilityQueries';
import { useVHAdjustment } from './utils/customHooks/useVHAdjustment';
import { useHandleNavigate } from './utils/customHooks/usHandleNavigate';
import { isUserDelegating, tokenUserName } from './utils/utils';

const App = () => {
  useVHAdjustment();
  const { token, decodedJWT } = useAuthContext();
  const { regExPattern } = useHandledLinksContext();
  const navigate = useNavigate();
  useHandleNavigate();

  const [getUserData, { data, called }] = useLazyQuery(GET_BASIC_USER_DATA, {
    fetchPolicy: 'cache-first',
  });

  const [loginPerformed, { data: loginResponse }] = useMutation(LOGIN_ACTION);

  useEffect(() => {
    if (!called) {
      getUserData();
    }
  }, [called, getUserData]);

  useEffect(() => {
    const username = tokenUserName(token);
    if (username && token && !isUserDelegating(token)) {
      const lastLoginAttempt = localStorage.getItem(username);
      if (!lastLoginAttempt || dayjs().diff(lastLoginAttempt, 'hours') >= 1) {
        loginPerformed();
      }
    }
  }, [loginPerformed, token]);

  useEffect(() => {
    const username = tokenUserName(token);
    if (
      username &&
      loginResponse &&
      loginResponse.loginActionPerformed?.__typename == 'LoginActionSuccess'
    ) {
      localStorage.setItem(username, dayjs().format());
    }
  }, [data, loginResponse, token]);

  useEffect(() => {
    const subscription = dx3Observables.handledNavEventObservable.subscribe(
      (str) => {
        if (str && regExPattern) {
          navigate(str.replace(regExPattern, '') || '/');
        }
      }
    );
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, regExPattern]);

  const redirect = useMemo(() => {
    return <Navigate replace to={ROUTES.HOME} />;
  }, []);

  const homeUrl = useMemo(() => {
    return getRootUrlByToken(decodedJWT)?.X3;
  }, [decodedJWT]);

  const shouldNotBeHere = (
    <Container className="no-access">
      <Typography variant="h2">{COPY.NO_ACCESS.HEADER}</Typography>
      <Link href={homeUrl}>{COPY.NO_ACCESS.LINK}</Link>
    </Container>
  );

  return (
    <div className="App">
      <dx3-application-header
        jwt-token={token}
        handled-link-regex={regExPattern?.source}
      />
      <RequireFeature featureKey={'DLE'} fallback={shouldNotBeHere}>
        <Routes>
          <Route
            path={ROUTES.HOME}
            element={
              <PrivateRoute>
                <HomeScreen />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ARTICLES}
            element={
              <PrivateRoute>
                <ArticleContainer />
              </PrivateRoute>
            }
          />
          <Route path="*" element={redirect} />
        </Routes>
      </RequireFeature>
    </div>
  );
};

export default App;
